import { Button, Card, CardContent, CardHeader, Divider, TextField, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { PageHeader } from 'pages/CustomerBookingCreate/components/PageHeader';
import { STEP_CART_ENABLED, STEP_SERVICE_COST, STEP_SERVICE_DETAILS, STEP_SERVICE_PACKAGES } from 'pages/CustomerBookingCreate/constants';
import { activeServiceState, bookingRegionState, bookingState, cartLoadingState, cartState, cartVisibleState, categoriesState, invalidCartState, packagesState, servicesState, sessionTokenState, stepState, verifyCartState } from 'pages/CustomerBookingCreate/state';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactJson from 'react-json-view';
import { useDraggable } from 'react-use-draggable-scroll';
import { useRecoilState } from 'recoil';
import { getServiceDetails } from 'services/service.service';
import { ServiceAutocompleteList, ServiceAutocompleteListAsDialog } from './ServiceAutocompleteList';
import { ServiceCard } from './ServiceCard';
import { PackageCategoryCard } from './PackageCategoryCard';
import isEmpty from 'lodash.isempty';
import useCustomModal from 'components/CustomConfirm';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';
import { useTrackEvent } from 'util/trackEvent';
import { verifyIfLocationSupported } from 'services/booking.service';
import { isAuthenticated, useUserState } from 'services/authentication.service';
import { useUpdateEffect } from "usehooks-ts";
import { calcBorderStyle, useScrollEdgeHook } from 'util/scrollFade';
import { useTheme } from "@material-ui/core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { CartButton } from 'pages/CustomerBookingCreate/components/PageHeader/CartButton';

const useStyles = makeStyles(theme => ({

  container: {
    display: "flex",
    overflowX: "scroll",
    "&::-webkit-scrollbar": {
      display: "none"
    },
    scrollbarWidth: "none",
    "-ms-overflow-style": "none",

    "&>*": {
      marginRight: "0.75rem"
    },
    padding: theme.spacing(3),
    marginTop: theme.spacing(-3),
  }
}));
const ServiceDetails = ({ }) => {
  const classes = useStyles()

  const [booking, setBooking] = useRecoilState(bookingState)
  const [packages, setPackages] = useRecoilState(packagesState);
  const [services, setServices] = useRecoilState(servicesState);
  const [cart, setCart] = useRecoilState(cartState);
  const [invalidCart, setInvalidCart] = useRecoilState(invalidCartState);
  const [step, setStep] = useRecoilState(stepState)
  const [service, setService] = useRecoilState(activeServiceState)

  const [categories, setCategories] = useRecoilState(categoriesState);
  const [enableCart, setEnableCart] = useRecoilState(cartVisibleState);

  const categoryRef = useRef(null)
  const { events: categoryEvents } = useDraggable(categoryRef)
  const packageRef = useRef(null)
  const { events: packageEvents } = useDraggable(packageRef)

  const [packageReachedStart, packageReachedEnd, { onScroll: packageOnScroll }, packClassName] = useScrollEdgeHook(packageRef)
  const [categoryReachedStart, categoryReachedEnd, { onScroll: categoryOnScroll }, catClassName] = useScrollEdgeHook(categoryRef)


  const [loading, setLoading] = useState(false);

  const [cartLoading, setCartLoading] = useRecoilState(cartLoadingState);

  const [selectedRegion, setSelectedRegion] = useRecoilState(bookingRegionState);



  const theme = useTheme()
  const packageContainerStyle = useMemo(() => {
    return calcBorderStyle(theme.shape.borderRadius, packageReachedStart, packageReachedEnd)
  }, [packageReachedStart, packageReachedEnd]);

  const categoryContainerStyle = useMemo(() => {
    return calcBorderStyle(theme.shape.borderRadius, categoryReachedStart, categoryReachedEnd)
  }, [categoryReachedStart, categoryReachedEnd]);


  useEffect(
    () => {
      setCartLoading(loading)
    }, [loading]
  );

  const fetchServiceDetails = useCallback(
    async () => {
      try {
        setLoading(true)
        const svc = await getServiceDetails({
          serviceId: booking?.serviceCategory?.type?.id || booking?.serviceCategory?.featured_service?.type_id,
          latitude: booking?.location?.latitude,
          longitude: booking?.location?.longitude
        })

        setService({
          A_Booking: booking?.location,
          ...svc,
        })
        setBooking({
          ...booking,
          serviceCategory: svc
        })

        setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    },
    [booking],
  );

  const { getUser } = useUserState()
  const [session_token, setSession_token] = useRecoilState(sessionTokenState);

  useEffect(
    () => {
      fetchServiceDetails()
    }, [booking?.location]
  );

  const confirm = useCustomModal()

  const trackEvent = useTrackEvent()

  const confirmIntersectingCart = useCallback(
    (intersectingcart) => {
      confirm(props => (
        <CustomConfirmDialog {...props}
          title="Similar individual tests on different packages"
          description={
            <>
              <div>
                Oops, looks like some individual tests are the same in the packages selected. Please review what you can get in each package by tapping the package card
              </div>
              <br />
              <Divider />
              <div>
                Identical packages in <i>
                  {intersectingcart?.map((item, index) => item?.label).join(', ')}
                </i>
              </div>
            </>
          }
          confirmationText='Ok'
          noCancel
          onConfirm={async () => {
            trackEvent('Create Booking Cart has intersecting items dialog - Yes', { dialog: ['InvalidLocationConfirm', 'doCancelBooking'] })
            props.onClose()
          }}
        />
      ))
    },
    [confirm, trackEvent],
  );
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });

  const SACList = useMemo(
    () => {
      const Component = isMobile ? ServiceAutocompleteListAsDialog : ServiceAutocompleteList

      const renderComponent = <Component
        isMobile={isMobile}
        compact={!isMobile}
        service={service}
        open={openAutocomplete}
        onSelect={async (s) => {

          setServices(s)
          setCart({
            services: s,
            packages,
            booking
          })
        }}
        selectedItems={services}
        onClose={() => setOpenAutocomplete(false)} >
        <Button
          onClick={() => setOpenAutocomplete(false)}
          color="primary"
          variant="contained"
          fullWidth
        >
          {services?.length > 0 ? 'Add selected to cart' : 'Close'}
        </Button>
      </Component>

      if (isMobile) {
        return (
          <>
            <Card>
              <CardContent>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    trackEvent('Create Booking Tests - Select individual tests - click')
                    setOpenAutocomplete(true)
                  }} >
                  {`Find ${service?.type?.label} By Name`}
                </Button>
              </CardContent>
            </Card>
            {renderComponent}
          </>
        )
      } else {
        return (
          <Card>
            <CardHeader title={`Find ${service?.type?.label} By Name`} />
            {renderComponent}
          </Card>
        )
      }
    }, [service, openAutocomplete, services, packages, booking]
  );

  return (
    <>
      <PageHeader
        showLocationSetter
        backStep={STEP_SERVICE_PACKAGES}
        currentStep={STEP_SERVICE_DETAILS}
        title={"Select tests"}
        subheader={"---"}
        callToActionButton={
          isMobile &&
          <Suspense fallback={<Button variant="contained" disabled fullWidth>Loading...</Button>}>
            <CartButton
              isCartEnabled={STEP_CART_ENABLED.includes(step)}
            />
          </Suspense>
        }
        isCartEnabled
        loading={loading}
      >
        {SACList}

        <div
          style={{
            position: 'relative'
          }}
        >
          <div
            className={clsx("hints-square", packClassName)}
            style={{
              ...packageContainerStyle,
              transition: 'box-shadow 0.3s',
            }}
          />
          <Card style={service?.extended_packages?.length > 0 ? {} : { display: 'none' }}>
            <CardHeader title="Zennya Packages" />
            <div className={classes.container}
              {...packageEvents}
              ref={packageRef}
              onScroll={packageOnScroll}
            >
              {service?.extended_packages?.map((category, index) => {
                const isSelected = !!packages?.find(x => x?.id === category?.id)
                return <ServiceCard
                  canAddToCart={enableCart}
                  key={index}
                  isSelected={isSelected}
                  baseFee={service?.base_fee}
                  service={category}
                  onSelect={async (card) => {
                    const intersectingPackages = verifyCartState({ services, packages, newServices: [], newPackages: [card] })
                    if (isEmpty(intersectingPackages)) {
                      setPackages([...packages, card])
                      setCart({
                        services,
                        packages: [...packages, card],
                        booking
                      })
                      setInvalidCart(null)
                    }
                    else {
                      setInvalidCart(intersectingPackages)
                      confirmIntersectingCart(intersectingPackages)
                    }
                  }}
                  onRemove={(card) => {
                    setPackages(packages?.filter(x => x?.id !== card?.id))
                    setCart({
                      services,
                      packages: packages?.filter(x => x?.id !== card?.id),
                      booking
                    })
                  }}
                />
              })}
            </div>
          </Card>
        </div>
        <div
          style={{
            position: 'relative'
          }}
        >
          <div
            className={clsx("hints-square", catClassName)}
            style={{
              ...categoryContainerStyle,
              transition: 'box-shadow 0.3s',
            }}
          />

          <Card>
            <CardHeader title="Package Categories" />
            <div className={classes.container}
              {...categoryEvents}
              ref={categoryRef}
              onScroll={categoryOnScroll}
            >
              {service?.extended_package_categories?.map((category, index) => (
                <PackageCategoryCard
                  services={service?.extended_package_items}
                  key={index}
                  service={category}
                  onSelect={async (cards) => {
                    setServices([...cards])
                    setCart({
                      services: cards,
                      packages,
                      booking
                    })
                  }}
                  selectedServices={services}
                />
              ))}
            </div>
          </Card>
        </div>

      </PageHeader>
    </>
  )
}

export { ServiceDetails };
